/**
 * ValuesReadFailed: a read of the latest values failed (the view tries again every 5 s by itself). It says the
 * values on screen are from the last read that worked, so a dead link no longer looks live, and offers "Try again".
 * The gateway sheet's tags and the instruments' values use it.
 *
 * The button turns only for its own read: the view's own tries run every few seconds, and a button that turned
 * for those would look busy all the time. A click starts a new read even while one of those runs.
 */

import { useState } from "react";
import { Loader2, RefreshCw, TriangleAlert } from "lucide-react";
import { Button } from "@/lib/ui/button";

export interface ValuesReadFailedProps {
  /** When the values on screen were read (epoch ms); 0 when no read worked. */
  shownAt: number;
  /** Read the values again now. */
  onRetry: () => Promise<unknown>;
  /** What the values are of, for the button's name ("Try again: the values of Panel 3"). */
  subject: string;
}

export function ValuesReadFailed({ shownAt, onRetry, subject }: ValuesReadFailedProps) {
  const [busy, setBusy] = useState(false);
  const at = shownAt > 0 ? new Date(shownAt).toLocaleTimeString(undefined, { timeStyle: "medium" }) : "";
  const retry = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onRetry();
    } finally {
      setBusy(false);
    }
  };
  return (
    <p role="status" className="flex flex-wrap items-center gap-x-2 text-[11px] text-on-surface">
      <TriangleAlert className="size-3.5 shrink-0 text-warning" aria-hidden />
      {at ? `The latest values could not be read. These are from ${at}.` : "The latest values could not be read."}
      <Button
        size="sm"
        variant="ghost"
        className="h-6 px-1.5 text-[11px]"
        aria-busy={busy}
        aria-label={`Try again: ${subject}`}
        onClick={() => void retry()}
      >
        {busy ? <Loader2 className="size-3 animate-spin" /> : <RefreshCw className="size-3" />}
        Try again
      </Button>
    </p>
  );
}
